/**
 * Legacy super-admin URLs → Hybrid destinations (epic cas-6ea3, W3, cas-2a55).
 *
 * The table is ia.md §5. Two legacy shapes are handled:
 *   1. the tab shell   — `/admin?tab=<key>`
 *   2. the direct pages — `/admin/<file>` (every old tab is also a Nuxt page
 *      because it lives under pages/admin/; those files are now mounted
 *      inside the destination pages, so their own routes forward too).
 *
 * `/admin` itself lands on Analytics › Product funnel, range Today (cas-eabc,
 * Ben × Daniel sync 2026-10-16 §A1; it was the Home until then): `/admin` with
 * no tab and `/admin?tab=<unknown>` both land there, so every entry point that
 * says `/admin` (sidebar, workspace sheet) arrives in one hop.
 *
 * The retire-proposed Queue, Blueprints and Viral Bank TABS forward to their
 * §5 destinations like every other tab; only their standalone PAGES
 * (`/admin/queue`, `/admin/blueprints`, `/admin/viral-bank`) stay unredirected
 * until Daniel rules on the §3 retire proposals — the destinations link to them.
 *
 * Pure: one function, no Nuxt imports, so the route middleware (SPA
 * navigation) and the Nitro server middleware (hard loads, curl proof)
 * cannot drift apart.
 *
 * cas-fd86: i18n serves every admin page under its locale prefix too
 * (`/es/admin/...`). A redirect keeps the prefix it came in on, so a Spanish
 * admin's direct link or reload stays in Spanish.
 */
import { NON_DEFAULT_LOCALE_CODES, stripLocalePrefix } from '~/utils/public-audit-path';
import {
  ANALYTICS_BASE_PATH,
  analyticsSectionForLegacyView,
  analyticsSectionPath,
  isAnalyticsSectionKey
} from '~/utils/admin-analytics-sections';

export interface AdminRedirectTarget {
  path: string;
  query?: Record<string, string>;
}

/** `/admin?tab=<key>` → destination. Keys are `SUPER_ADMIN_TABS` entries. */
export const LEGACY_TAB_REDIRECTS: Readonly<Record<string, AdminRedirectTarget>> = {
  analytics: { path: '/admin/business/analytics' },
  promos: { path: '/admin/business/pricing', query: { view: 'promos' } },
  marketPricing: { path: '/admin/business/pricing', query: { view: 'countries' } },
  tools: { path: '/admin/content-lab' },
  auditV2Workbench: { path: '/admin/content-lab', query: { tool: 'audit-v3' } },
  users: { path: '/admin/people/users' },
  posts: { path: '/admin/people/posts' },
  sms: { path: '/admin/messaging/outbound', query: { view: 'outbound' } },
  smsCampaigns: {
    path: '/admin/messaging/outbound',
    query: { view: 'outbound', action: 'send-test' }
  },
  conversionSms: { path: '/admin/messaging/sequences', query: { view: 'onboarding' } },
  continuePath: { path: '/admin/messaging/sequences', query: { view: 'continue-path' } },
  dailyAudit: { path: '/admin/messaging/programs', query: { view: 'daily-audit' } },
  contentRoutines: { path: '/admin/content-routines' },
  viralSms: { path: '/admin/messaging/programs', query: { view: 'viral-sms' } },
  smsOutreach: { path: '/admin/messaging/programs', query: { view: 'outreach' } },
  reengagement: { path: '/admin/messaging/programs', query: { view: 'reengagement' } },
  email: { path: '/admin/messaging/email' },
  featureFlags: { path: '/admin/system/flags' },
  cache: { path: '/admin/system/health', query: { view: 'cache' } },
  webhooks: { path: '/admin/business/webhooks' },
  queue: { path: '/admin/system/health', query: { view: 'cron' } },
  blueprints: { path: '/admin/content-lab', query: { tool: 'blueprints' } },
  viralBank: { path: '/admin/content-lab', query: { tool: 'viral-bank' } }
};

/** The Hybrid home destination (the shell's brand link). */
export const ADMIN_HOME: AdminRedirectTarget = { path: '/admin/home' };

/**
 * `/admin` with no tab, or an unknown tab: Analytics › Product funnel on Today
 * (cas-eabc, Ben × Daniel sync 2026-10-16 §A1: "when I open the admin section it
 * should default to analytics with the product funnel view with the filter
 * defaulted to today"). Supersedes the 2026-09-09 "/admin is the Home" decision.
 */
export const ADMIN_LANDING: AdminRedirectTarget = {
  path: analyticsSectionPath('funnel'),
  query: { range: 'today' }
};

/**
 * Retire-proposed tabs (ia.md §3): their `?tab=` URLs forward like the rest;
 * their standalone pages stay reachable until Daniel rules on retirement.
 */
export const RETIRE_PROPOSED_TABS = ['queue', 'blueprints', 'viralBank'] as const;

/** `/admin/<file>` → the same destinations (pages/admin/<file>.vue is now mounted there). */
export const LEGACY_PATH_REDIRECTS: Readonly<Record<string, AdminRedirectTarget>> = {
  '/admin/analytics': LEGACY_TAB_REDIRECTS.analytics!,
  '/admin/PromoCodes': LEGACY_TAB_REDIRECTS.promos!,
  '/admin/market-pricing': LEGACY_TAB_REDIRECTS.marketPricing!,
  '/admin/tools': LEGACY_TAB_REDIRECTS.tools!,
  '/admin/audit-v3-workbench': LEGACY_TAB_REDIRECTS.auditV2Workbench!,
  '/admin/audit-v2-workbench': LEGACY_TAB_REDIRECTS.auditV2Workbench!,
  '/admin/script-lab-workbench': { path: '/admin/content-lab', query: { tool: 'script-lab' } },
  '/admin/users': LEGACY_TAB_REDIRECTS.users!,
  '/admin/sms': LEGACY_TAB_REDIRECTS.sms!,
  '/admin/sms-campaigns': LEGACY_TAB_REDIRECTS.smsCampaigns!,
  '/admin/conversion-sms': LEGACY_TAB_REDIRECTS.conversionSms!,
  '/admin/continue-path': LEGACY_TAB_REDIRECTS.continuePath!,
  '/admin/daily-audit': LEGACY_TAB_REDIRECTS.dailyAudit!,
  '/admin/viral-inspiration': LEGACY_TAB_REDIRECTS.viralSms!,
  '/admin/sms-outreach': LEGACY_TAB_REDIRECTS.smsOutreach!,
  '/admin/reengagement': LEGACY_TAB_REDIRECTS.reengagement!,
  '/admin/email': LEGACY_TAB_REDIRECTS.email!,
  '/admin/feature-flags': LEGACY_TAB_REDIRECTS.featureFlags!,
  '/admin/cache': LEGACY_TAB_REDIRECTS.cache!,
  '/admin/webhooks': LEGACY_TAB_REDIRECTS.webhooks!
};

type QueryValue = string | string[] | null | undefined;
export type AdminRedirectQuery = Record<string, QueryValue>;

function first(v: QueryValue): string | undefined {
  if (Array.isArray(v)) return typeof v[0] === 'string' ? v[0] : undefined;
  return typeof v === 'string' ? v : undefined;
}

function normalizePath(path: string): string {
  const clean = path.split('?')[0]?.split('#')[0] ?? path;
  return clean.length > 1 && clean.endsWith('/') ? clean.slice(0, -1) : clean;
}

/**
 * The destination URL for a legacy admin URL, or null when the URL is not
 * legacy (new destinations, the home, retire-proposed standalone pages).
 * `/admin` itself always resolves: a known tab to its destination, no tab or
 * an unknown tab to the Home. The incoming query survives; the destination's
 * own keys win on a clash and `tab` is dropped.
 */
/** The locale prefix a path carries (`/es`), or '' for the default locale. */
export function localeAdminPrefix(path: string): string {
  for (const code of NON_DEFAULT_LOCALE_CODES) {
    const prefix = `/${code}`;
    if (path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`)) {
      return prefix;
    }
  }
  return '';
}

/** True for `/admin...` with or without a locale prefix (`/es/admin...`). */
export function isAdminPath(path: string): boolean {
  return stripLocalePrefix(path).startsWith('/admin');
}

export function resolveAdminRedirect(path: string, query: AdminRedirectQuery = {}): string | null {
  const prefix = localeAdminPrefix(path);
  const target = resolveUnprefixedAdminRedirect(
    prefix ? stripLocalePrefix(normalizePath(path)) : path,
    query
  );
  return target && prefix ? `${prefix}${target}` : target;
}

function resolveUnprefixedAdminRedirect(path: string, query: AdminRedirectQuery): string | null {
  const p = normalizePath(path);
  let target: AdminRedirectTarget | undefined;
  if (p === '/admin/content-lab' && first(query.tool) === 'audit-v2') {
    target = LEGACY_TAB_REDIRECTS.auditV2Workbench;
  } else if (p === '/admin') {
    const tab = first(query.tab);
    target = tab ? (LEGACY_TAB_REDIRECTS[tab] ?? ADMIN_LANDING) : ADMIN_LANDING;
  } else {
    target = LEGACY_PATH_REDIRECTS[p];
  }
  // cas-40e4: Analytics is four sub-pages now. The bare URL and every legacy
  // alias land on one of them; the old `?view=funnel` chip opens Product funnel.
  let dropView = false;
  if (p === ANALYTICS_BASE_PATH || target?.path === ANALYTICS_BASE_PATH) {
    target = {
      ...target,
      path: analyticsSectionPath(analyticsSectionForLegacyView(first(query.view)))
    };
    dropView = true;
  } else if (
    p.startsWith(`${ANALYTICS_BASE_PATH}/`) &&
    !isAnalyticsSectionKey(p.slice(ANALYTICS_BASE_PATH.length + 1))
  ) {
    target = { path: analyticsSectionPath(analyticsSectionForLegacyView(undefined)) };
  }
  if (!target) return null;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (key === 'tab' || (dropView && key === 'view') || target.query?.[key] !== undefined)
      continue;
    const values = Array.isArray(value) ? value : [value];
    for (const v of values) if (typeof v === 'string') params.append(key, v);
  }
  const merged = new URLSearchParams(target.query ?? {});
  for (const [k, v] of params) merged.append(k, v);
  const qs = merged.toString();
  return qs ? `${target.path}?${qs}` : target.path;
}
